// Per-app reference status chips and strips (spec 6.10.19.3, feature spec 15); the texts come from the delivery
// registry, nothing here branches on a platform name.
import {
  ArrowLeft,
  CircleCheck,
  CircleDashed,
  CircleX,
  History,
  Info,
  RefreshCw,
  TriangleAlert,
  X,
} from 'lucide-react';
import type { ReactNode } from 'react';
import type { SettingDelivery } from '../api/types';
import { IconButton } from '../components/ui';
import { formatDateTime, formatRelative } from '../lib/format';
import { strings } from '../strings';
import { appStatusParts, appStatusText } from './appStatusModel';
import type { VariableApp, VariableRow } from './model';
import type { DeliveryTypeDefinition } from './registry';
import { isRollingOut } from './useVariablesData';

const v = strings.vaults;

const TONE_ICONS = {
  success: CircleCheck,
  info: CircleDashed,
  warning: TriangleAlert,
  danger: CircleX,
  neutral: CircleDashed,
} as const;

/** The status chip of an app (strip and group row). */
export function AppStatusChip({
  app,
  entry,
}: {
  app: Pick<VariableApp, 'resourceId' | 'appReferenceStatus' | 'lastRestart'>;
  entry: DeliveryTypeDefinition | null;
}) {
  const text = appStatusText(app, entry);
  if (!text) return null;
  const Icon = isRollingOut(app) ? RefreshCw : TONE_ICONS[text.tone];
  return (
    <span
      className={`badge tone-${text.tone}`}
      title={text.help ?? undefined}
      data-testid={`app-status-${app.resourceId}`}
    >
      <Icon aria-hidden className={`size-3 ${isRollingOut(app) ? 'animate-spin' : ''}`} />
      {text.label}
    </span>
  );
}

/**
 * One app strip (mocks r1-uiux-ecs-app-strip): "<app> · <delivery label> · <status chip> · checked <n> ago ·
 * oldest task started <t> ago", with the rollout action at the right. Not a live region.
 */
export function AppStrip({
  app,
  delivery,
  entry,
  checkedAt,
  stillRollingOut,
  action,
  onDetails,
}: {
  app: VariableApp;
  delivery: SettingDelivery;
  entry: DeliveryTypeDefinition;
  checkedAt: string | null;
  stillRollingOut: boolean;
  action: ReactNode;
  /** Opens the app's details (the strip's (i) button). */
  onDetails?: () => void;
}) {
  const readAt = app.appReferenceStatus?.readAt ?? null;
  const cloudRun = delivery.platform === 'cloud-run';
  const parts = [
    checkedAt ? v.checked(formatRelative(checkedAt)) : null,
    readAt
      ? cloudRun
        ? v.revisionCreated(formatRelative(readAt))
        : v.oldestTask(formatRelative(readAt))
      : null,
  ].filter(Boolean);
  return (
    <div
      className="var-strip tone-neutral"
      data-testid={`app-strip-${app.resourceId}`}
      aria-label={v.appStrip(app.resourceId)}
    >
      <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
        <span className="font-mono font-semibold">{app.resourceId}</span>
        <span className="text-fg-muted">· {entry.label(delivery.mode, delivery)} ·</span>
        <AppStatusChip app={app} entry={entry} />
        {parts.length > 0 && <span className="text-fg-muted">· {parts.join(' · ')}</span>}
        {stillRollingOut && <span className="text-fg-muted">· {v.stillRollingOut}</span>}
      </span>
      {onDetails && (
        <IconButton
          label={v.appDetails(app.resourceId)}
          onClick={onDetails}
          data-testid={`app-details-${app.resourceId}`}
        >
          <Info aria-hidden className="size-4" />
        </IconButton>
      )}
      {action}
    </div>
  );
}

/**
 * The app's details (spec 6.10.19.3): the delivery label and pickup sentence, the status with its explanation and the
 * platform's raw details, when the app read its references, the rows that use an older version, and the rollout.
 */
export function AppDetailsPane({
  app,
  delivery,
  entry,
  rows,
  checkedAt,
  rollout,
  narrow,
  fullscreen = false,
  onOpenRow,
  onClose,
}: {
  app: VariableApp;
  delivery: SettingDelivery | null;
  entry: DeliveryTypeDefinition | null;
  /** The app's rows. */
  rows: readonly VariableRow[];
  checkedAt: string | null;
  rollout: ReactNode;
  narrow: boolean;
  fullscreen?: boolean;
  onOpenRow: (rowId: string) => void;
  onClose: () => void;
}) {
  const title = v.appDetails(app.resourceId);
  const status = app.appReferenceStatus ?? null;
  const text = appStatusText(app, entry);
  const older = rows.filter((r) => r.olderVersionInUse);
  const readAt = status?.readAt ?? null;
  return (
    <aside
      aria-label={title}
      className={`var-details ${narrow ? 'var-details-narrow' : ''} ${fullscreen ? 'var-details-fullscreen' : ''}`}
      data-testid="app-details"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !event.defaultPrevented) {
          event.preventDefault();
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="var-details-head">
        {narrow && (
          <IconButton label={strings.variables.details.back} onClick={onClose}>
            <ArrowLeft aria-hidden className="size-4" />
          </IconButton>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[14px] font-semibold">{title}</h3>
          {delivery && entry && (
            <p className="truncate text-[12px] text-fg-muted">{entry.label(delivery.mode, delivery)}</p>
          )}
        </div>
        {!narrow && (
          <IconButton label={strings.variables.details.close} onClick={onClose}>
            <X aria-hidden className="size-4" />
          </IconButton>
        )}
      </div>
      <div className="var-details-body">
        {delivery && entry && (
          <section className="var-details-section">
            <h4 className="var-details-heading">{v.appDetailsDelivery}</h4>
            <p>{entry.label(delivery.mode, delivery)}</p>
            <p className="mt-0.5 text-[12px] text-fg-muted">{entry.pickupText([app.resourceId], delivery)}</p>
          </section>
        )}
        <section className="var-details-section">
          <h4 className="var-details-heading">{v.appDetailsStatus}</h4>
          {status ? (
            <>
              <p className="flex flex-wrap items-center gap-1.5">
                <AppStatusChip app={app} entry={entry} />
                {appStatusParts(app, delivery, checkedAt).length > 0 && (
                  <span className="text-[12px] text-fg-muted">
                    · {appStatusParts(app, delivery, checkedAt).join(' · ')}
                  </span>
                )}
              </p>
              {text?.help && <p className="mt-1 text-[12px] text-fg-muted">{text.help}</p>}
              <p className="mt-1 text-[12px] text-fg-muted" data-testid="app-details-raw">
                {v.appDetailsRawStatus(status.status)}
              </p>
              {status.details && (
                <p className="mt-0.5 font-mono text-[11.5px] break-words text-fg-muted">{status.details}</p>
              )}
            </>
          ) : (
            <p className="text-fg-muted">{v.appDetailsNoStatus}</p>
          )}
        </section>
        {readAt && (
          <section className="var-details-section">
            <h4 className="var-details-heading">{v.appDetailsReadAt}</h4>
            <p data-testid="app-details-read-at">
              {v.appDetailsReadAtValue(formatDateTime(readAt), formatRelative(readAt))}
            </p>
          </section>
        )}
        <section className="var-details-section">
          <h4 className="var-details-heading">{v.appDetailsOlder}</h4>
          {older.length === 0 ? (
            <p className="text-fg-muted">{v.appDetailsNoOlder}</p>
          ) : (
            <ul className="space-y-1">
              {older.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    className="link inline-flex items-center gap-1 font-mono"
                    onClick={() => onOpenRow(row.id)}
                  >
                    <History aria-hidden className="size-3.5" />
                    {row.displayName}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
        {rollout && (
          <section className="var-details-section">
            <h4 className="var-details-heading">{v.appDetailsRollout}</h4>
            <div className="flex flex-wrap gap-1.5">{rollout}</div>
          </section>
        )}
      </div>
    </aside>
  );
}
